import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select, delay } from 'redux-saga/effects';
import { SkuCode, SkuSyncResult, TrackingRunStarted, SchedulerRunStatus, SchedulerRunHistoryItem, CardFundingBackfillStarted, CardFundingBackfillStatus, CardFundingBackfillHistoryItem, ChargeReconciliationRunHistoryItem, OrphanedCharge, ReverseChargeResult } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import { selectIdToken, unauthorized } from '../../auth/redux/slice';
import { toast } from 'react-hot-toast';
import platformMaintenanceSlice, {
    loadSkus, syncSkus, loadSchedulerHistory, runSchedulerNow, loadSchedulerStatus, selectPlatformMaintenanceSchedulerHistory,
    loadCardFundingHistory, runCardFundingBackfill, loadCardFundingStatus, selectPlatformMaintenanceCardFundingHistory,
    loadOrphanedChargesHistory, runOrphanedChargesCheck, loadOrphanedChargesStatus, selectPlatformMaintenanceOrphanedChargesHistory,
    loadOrphanedChargesOutstanding, reverseOrphanedCharge, loadOrphanedChargesResolved,
} from './slice';

export abstract class PlatformMaintenanceSagas {
    public static* run(){
        try {
            yield all([
                takeLatest(loadSkus, PlatformMaintenanceSagas.loadSkus),
                takeLatest(syncSkus, PlatformMaintenanceSagas.syncSkus),
                takeLatest(loadSchedulerHistory, PlatformMaintenanceSagas.loadSchedulerHistory),
                takeLatest(runSchedulerNow, PlatformMaintenanceSagas.runSchedulerNow),
                takeLatest(loadSchedulerStatus, PlatformMaintenanceSagas.loadSchedulerStatus),
                takeLatest(loadCardFundingHistory, PlatformMaintenanceSagas.loadCardFundingHistory),
                takeLatest(runCardFundingBackfill, PlatformMaintenanceSagas.runCardFundingBackfill),
                takeLatest(loadCardFundingStatus, PlatformMaintenanceSagas.loadCardFundingStatus),
                takeLatest(loadOrphanedChargesHistory, PlatformMaintenanceSagas.loadOrphanedChargesHistory),
                takeLatest(runOrphanedChargesCheck, PlatformMaintenanceSagas.runOrphanedChargesCheck),
                takeLatest(loadOrphanedChargesStatus, PlatformMaintenanceSagas.loadOrphanedChargesStatus),
                takeLatest(loadOrphanedChargesOutstanding, PlatformMaintenanceSagas.loadOrphanedChargesOutstanding),
                takeLatest(reverseOrphanedCharge, PlatformMaintenanceSagas.reverseOrphanedCharge),
                takeLatest(loadOrphanedChargesResolved, PlatformMaintenanceSagas.loadOrphanedChargesResolved),
            ]);
        }
        catch(e: any){
            console.log(`[PlatformMaintenanceSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadSkus(){
        try {
            yield put(platformMaintenanceSlice.actions.setSkusState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<SkuCode[]> = yield call(
                { context: client, fn: client.getSkus },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setSkus(response.data));
            yield put(platformMaintenanceSlice.actions.setSkusState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setSkusState('ERROR'));
            toast.error('Error loading SKUs');
            console.log(`[PlatformMaintenanceSagas:loadSkus] the following exception has occurred`, e);
        }
    }

    private static* syncSkus(){
        try {
            yield put(platformMaintenanceSlice.actions.setSkusState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<SkuSyncResult> = yield call(
                { context: client, fn: client.syncSkus },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200){
                toast.error(typeof response.data === 'string' ? response.data : 'Error syncing SKUs');
                yield put(platformMaintenanceSlice.actions.setSkusState('ERROR'));
                return;
            }

            yield put(platformMaintenanceSlice.actions.setLastSyncResult(response.data));
            yield put(platformMaintenanceSlice.actions.setSkus(response.data.skus));
            yield put(platformMaintenanceSlice.actions.setSkusState('READY'));
            toast.success(`Synced ${response.data.created} new, ${response.data.updated} updated SKUs`);
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setSkusState('ERROR'));
            toast.error('Error syncing SKUs');
            console.log(`[PlatformMaintenanceSagas:syncSkus] the following exception has occurred`, e);
        }
    }

    private static* loadSchedulerHistory(){
        try {
            yield put(platformMaintenanceSlice.actions.setSchedulerHistoryState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<SchedulerRunHistoryItem[]> = yield call(
                { context: client, fn: client.getTrackingSchedulerHistory },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setSchedulerHistory(response.data));
            yield put(platformMaintenanceSlice.actions.setSchedulerHistoryState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setSchedulerHistoryState('ERROR'));
            toast.error('Error loading run history');
            console.log(`[PlatformMaintenanceSagas:loadSchedulerHistory] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - the server now kicks off the actual UPS check in the background and returns
    // almost immediately (see TrackingSchedulerRunCoordinator) instead of awaiting the whole run
    // inside the request - that's what used to make "Run Now" take minutes and hit a reverse
    // proxy timeout (502 Proxy Error) at real order volume. Completion is now detected via the
    // status endpoint (isRunning flips back to false) rather than diffing history timestamps -
    // simpler, and it's the same signal the drawer's own ambient poll uses (see
    // loadSchedulerStatus below), so "this session just triggered a run" and "a run is in
    // progress" can never disagree.
    private static* runSchedulerNow(){
        try {
            yield put(platformMaintenanceSlice.actions.setSchedulerRunState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<TrackingRunStarted> = yield call(
                { context: client, fn: client.runTrackingSchedulerManually },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200 && response.status !== 202){
                toast.error(typeof response.data === 'string' ? response.data : 'Error running tracking check');
                yield put(platformMaintenanceSlice.actions.setSchedulerRunState('ERROR'));
                return;
            }

            // Reflected immediately rather than waiting for the drawer's own next poll tick - this
            // session should see its own trigger take effect right away.
            yield put(platformMaintenanceSlice.actions.setSchedulerStatus({ isRunning: true, runningSinceUtc: new Date().toISOString() }));

            if (response.data.alreadyRunning) {
                toast('A tracking check is already running.');
                yield put(platformMaintenanceSlice.actions.setSchedulerRunState('READY'));
                return;
            }

            // Capped rather than infinite, in case something breaks server-side and IsRunning
            // never clears - ~10 minutes at 15s apart, since a real parallelized run over hundreds
            // of orders can still legitimately take several minutes.
            const MAX_POLL_ATTEMPTS = 40;
            let finished = false;
            for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS && !finished; attempt++) {
                yield delay(15000);

                const statusResponse: AxiosResponse<SchedulerRunStatus> = yield call(
                    { context: client, fn: client.getTrackingSchedulerStatus },
                );
                if (statusResponse.status !== 200) continue;

                yield put(platformMaintenanceSlice.actions.setSchedulerStatus(statusResponse.data));
                finished = !statusResponse.data.isRunning;
            }

            yield put(platformMaintenanceSlice.actions.setSchedulerRunState('READY'));

            if (!finished) {
                toast('Still running - check back in a moment.');
                return;
            }

            yield call(PlatformMaintenanceSagas.loadSchedulerHistory);
            const history: SchedulerRunHistoryItem[] = yield select(selectPlatformMaintenanceSchedulerHistory);
            const latest = history[0];
            if (latest) {
                toast.success(`Checked ${latest.ordersChecked} order${latest.ordersChecked !== 1 ? 's' : ''}, ${latest.ordersUpdated} updated${latest.errors > 0 ? `, ${latest.errors} error${latest.errors !== 1 ? 's' : ''}` : ''}`);
            }
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setSchedulerRunState('ERROR'));
            toast.error('Error running tracking check');
            console.log(`[PlatformMaintenanceSagas:runSchedulerNow] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - polled by the drawer while open (see SchedulerDrawer's effect) so any admin sees
    // a run in progress regardless of who/what triggered it. Deliberately silent on failure - a
    // missed status tick just means the "in progress" indicator is a few seconds stale, not worth
    // a toast interrupting whoever's just watching the drawer.
    private static* loadSchedulerStatus(){
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<SchedulerRunStatus> = yield call(
                { context: client, fn: client.getTrackingSchedulerStatus },
            );

            if (response.status === 401) {
                yield put(unauthorized(undefined));
                return;
            }
            if (response.status !== 200) return;

            yield put(platformMaintenanceSlice.actions.setSchedulerStatus(response.data));
        }
        catch(e: any){
            console.log(`[PlatformMaintenanceSagas:loadSchedulerStatus] the following exception has occurred`, e);
        }
    }

    private static* loadCardFundingHistory(){
        try {
            yield put(platformMaintenanceSlice.actions.setCardFundingHistoryState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<CardFundingBackfillHistoryItem[]> = yield call(
                { context: client, fn: client.getCardFundingBackfillHistory },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setCardFundingHistory(response.data));
            yield put(platformMaintenanceSlice.actions.setCardFundingHistoryState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setCardFundingHistoryState('ERROR'));
            toast.error('Error loading run history');
            console.log(`[PlatformMaintenanceSagas:loadCardFundingHistory] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - same background-run/poll-for-completion shape as runSchedulerNow above, and for
    // the same reason (a live Stripe lookup per org can outlast a reverse proxy timeout if run
    // inside the request).
    private static* runCardFundingBackfill(){
        try {
            yield put(platformMaintenanceSlice.actions.setCardFundingRunState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<CardFundingBackfillStarted> = yield call(
                { context: client, fn: client.backfillCardFunding },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200 && response.status !== 202){
                toast.error(typeof response.data === 'string' ? response.data : 'Error running card funding backfill');
                yield put(platformMaintenanceSlice.actions.setCardFundingRunState('ERROR'));
                return;
            }

            yield put(platformMaintenanceSlice.actions.setCardFundingStatus({ isRunning: true, runningSinceUtc: new Date().toISOString() }));

            if (response.data.alreadyRunning) {
                toast('A card funding backfill is already running.');
                yield put(platformMaintenanceSlice.actions.setCardFundingRunState('READY'));
                return;
            }

            // Capped rather than infinite, in case something breaks server-side and IsRunning
            // never clears - matches the tracking scheduler's own cap (~10 minutes at 15s apart).
            const MAX_POLL_ATTEMPTS = 40;
            let finished = false;
            for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS && !finished; attempt++) {
                yield delay(15000);

                const statusResponse: AxiosResponse<CardFundingBackfillStatus> = yield call(
                    { context: client, fn: client.getCardFundingBackfillStatus },
                );
                if (statusResponse.status !== 200) continue;

                yield put(platformMaintenanceSlice.actions.setCardFundingStatus(statusResponse.data));
                finished = !statusResponse.data.isRunning;
            }

            yield put(platformMaintenanceSlice.actions.setCardFundingRunState('READY'));

            if (!finished) {
                toast('Still running - check back in a moment.');
                return;
            }

            yield call(PlatformMaintenanceSagas.loadCardFundingHistory);
            const history: CardFundingBackfillHistoryItem[] = yield select(selectPlatformMaintenanceCardFundingHistory);
            const latest = history[0];
            if (latest) {
                toast.success(`Checked ${latest.checked} card${latest.checked !== 1 ? 's' : ''}, ${latest.updated} updated${latest.errors > 0 ? `, ${latest.errors} error${latest.errors !== 1 ? 's' : ''}` : ''}`);
            }
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setCardFundingRunState('ERROR'));
            toast.error('Error running card funding backfill');
            console.log(`[PlatformMaintenanceSagas:runCardFundingBackfill] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - polled by the drawer while open, same reasoning as loadSchedulerStatus above:
    // any admin should see a run in progress regardless of who/what triggered it. Deliberately
    // silent on failure - a missed status tick just means the indicator is a few seconds stale.
    private static* loadCardFundingStatus(){
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<CardFundingBackfillStatus> = yield call(
                { context: client, fn: client.getCardFundingBackfillStatus },
            );

            if (response.status === 401) {
                yield put(unauthorized(undefined));
                return;
            }
            if (response.status !== 200) return;

            yield put(platformMaintenanceSlice.actions.setCardFundingStatus(response.data));
        }
        catch(e: any){
            console.log(`[PlatformMaintenanceSagas:loadCardFundingStatus] the following exception has occurred`, e);
        }
    }

    private static* loadOrphanedChargesHistory(){
        try {
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesHistoryState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<ChargeReconciliationRunHistoryItem[]> = yield call(
                { context: client, fn: client.getOrphanedChargesHistory },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesHistory(response.data));
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesHistoryState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesHistoryState('ERROR'));
            toast.error('Error loading run history');
            console.log(`[PlatformMaintenanceSagas:loadOrphanedChargesHistory] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - same background-run/poll-for-completion shape as runSchedulerNow above, and for
    // the same reason (the reconciliation pass, and any Stripe refunds it issues, can outlast a
    // reverse proxy timeout if run inside the request).
    private static* runOrphanedChargesCheck(){
        try {
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesRunState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<TrackingRunStarted> = yield call(
                { context: client, fn: client.runOrphanedChargesCheckManually },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200 && response.status !== 202){
                toast.error(typeof response.data === 'string' ? response.data : 'Error running orphaned charge check');
                yield put(platformMaintenanceSlice.actions.setOrphanedChargesRunState('ERROR'));
                return;
            }

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesStatus({ isRunning: true, runningSinceUtc: new Date().toISOString() }));

            if (response.data.alreadyRunning) {
                toast('An orphaned charge check is already running.');
                yield put(platformMaintenanceSlice.actions.setOrphanedChargesRunState('READY'));
                return;
            }

            const MAX_POLL_ATTEMPTS = 40;
            let finished = false;
            for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS && !finished; attempt++) {
                yield delay(15000);

                const statusResponse: AxiosResponse<SchedulerRunStatus> = yield call(
                    { context: client, fn: client.getOrphanedChargesStatus },
                );
                if (statusResponse.status !== 200) continue;

                yield put(platformMaintenanceSlice.actions.setOrphanedChargesStatus(statusResponse.data));
                finished = !statusResponse.data.isRunning;
            }

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesRunState('READY'));

            if (!finished) {
                toast('Still running - check back in a moment.');
                return;
            }

            yield call(PlatformMaintenanceSagas.loadOrphanedChargesHistory);
            // Sep 2026 - detect-only: a finished run only found/flagged candidates, never reversed
            // anything - refresh the live worklist too so the drawer's "Needs Review" list reflects
            // whatever this run just found, without the admin having to close and reopen it.
            yield call(PlatformMaintenanceSagas.loadOrphanedChargesOutstanding);
            const history: ChargeReconciliationRunHistoryItem[] = yield select(selectPlatformMaintenanceOrphanedChargesHistory);
            const latest = history[0];
            if (latest) {
                toast.success(latest.chargesFound > 0
                    ? `Found ${latest.chargesFound} orphaned charge${latest.chargesFound !== 1 ? 's' : ''} awaiting review`
                    : 'No orphaned charges found');
            }
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesRunState('ERROR'));
            toast.error('Error running orphaned charge check');
            console.log(`[PlatformMaintenanceSagas:runOrphanedChargesCheck] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - polled by the drawer while open, same reasoning as loadSchedulerStatus above: any
    // admin should see a run in progress regardless of who/what triggered it. Deliberately silent
    // on failure - a missed status tick just means the indicator is a few seconds stale.
    private static* loadOrphanedChargesStatus(){
        try {
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const response: AxiosResponse<SchedulerRunStatus> = yield call(
                { context: client, fn: client.getOrphanedChargesStatus },
            );

            if (response.status === 401) {
                yield put(unauthorized(undefined));
                return;
            }
            if (response.status !== 200) return;

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesStatus(response.data));
        }
        catch(e: any){
            console.log(`[PlatformMaintenanceSagas:loadOrphanedChargesStatus] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - the live "Needs Review" worklist - computed fresh server-side on every call, not
    // dependent on a detection run having happened recently.
    private static* loadOrphanedChargesOutstanding(){
        try {
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesOutstandingState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<OrphanedCharge[]> = yield call(
                { context: client, fn: client.getOrphanedChargesOutstanding },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesOutstanding(response.data));
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesOutstandingState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesOutstandingState('ERROR'));
            toast.error('Error loading orphaned charges');
            console.log(`[PlatformMaintenanceSagas:loadOrphanedChargesOutstanding] the following exception has occurred`, e);
        }
    }

    // Sep 2026 - a Portal Admin reversing one charge from the "Needs Review" worklist, with a
    // typed reason - the same ChargeReversalService/endpoint ClinicSubmission.tsx's own automatic
    // path uses, just with a real reviewer and justification attached (see
    // OrganizationBillingHistory.ReversalReason/ReversedByUserId).
    private static* reverseOrphanedCharge(action: ReturnType<typeof reverseOrphanedCharge>){
        try {
            yield put(platformMaintenanceSlice.actions.setOrphanedChargeReversalState('BUSY'));
            yield put(platformMaintenanceSlice.actions.setReversingTransactionToken(action.payload.transactionToken));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<ReverseChargeResult> = yield call(
                { context: client, fn: client.reverseSubmissionFeeCharge },
                { transactionToken: action.payload.transactionToken, reason: action.payload.reason },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200){
                toast.error(typeof response.data === 'string' ? response.data : 'Error reversing charge');
                yield put(platformMaintenanceSlice.actions.setOrphanedChargeReversalState('ERROR'));
                return;
            }

            toast.success(response.data.alreadyReversed
                ? 'This charge was already reversed.'
                : `Reversed - $${response.data.stripeAmountRefunded.toFixed(2)} refunded${response.data.testCreditRestored > 0 ? `, $${response.data.testCreditRestored.toFixed(2)} credit restored` : ''}`);

            yield put(platformMaintenanceSlice.actions.setOrphanedChargeReversalState('READY'));
            yield call(PlatformMaintenanceSagas.loadOrphanedChargesOutstanding);
            // Sep 2026 - the row that just left the "Needs Review" list belongs on the "History"
            // tab now - refresh it too so it's there without the admin having to switch away and
            // back.
            yield call(PlatformMaintenanceSagas.loadOrphanedChargesResolved);
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setOrphanedChargeReversalState('ERROR'));
            toast.error('Error reversing charge');
            console.log(`[PlatformMaintenanceSagas:reverseOrphanedCharge] the following exception has occurred`, e);
        }
        finally {
            yield put(platformMaintenanceSlice.actions.setReversingTransactionToken(undefined));
        }
    }

    // Sep 2026 - the "History" tab - charges already actioned (reversed), newest first, with who
    // reversed it and why.
    private static* loadOrphanedChargesResolved(){
        try {
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesResolvedState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const response: AxiosResponse<OrphanedCharge[]> = yield call(
                { context: client, fn: client.getOrphanedChargesResolved },
            );

            if(response.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(response.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(platformMaintenanceSlice.actions.setOrphanedChargesResolved(response.data));
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesResolvedState('READY'));
        }
        catch(e: any){
            yield put(platformMaintenanceSlice.actions.setOrphanedChargesResolvedState('ERROR'));
            toast.error('Error loading orphaned charge history');
            console.log(`[PlatformMaintenanceSagas:loadOrphanedChargesResolved] the following exception has occurred`, e);
        }
    }
}
